"use client";
import { useForm } from "react-hook-form";
import { AmountComponent } from "./amountComponent";
import { Merchant } from "@/dto/merchant";
import { getCurrencyCodeFromCountry } from "@/utils/currencyUtils";
import { PageHeading } from "./pageHeading";
import { Countries } from "country-to-currency";
import { BuyThisForMyselfComponent } from "./buyThisForMyselfComponent";
import { useEffect, useMemo, useState } from "react";
import { RecipientInformationComponent } from "./recipientInformationComponent";
import { GiftCardImage } from "./giftCardImage";
import { AddGiftCardButton } from "./addGiftCardButton";
import { useRouter } from "next13-progressbar";
import { ReplaceGiftCardModal } from "./replaceGiftCardModal";
import { ClearCartModal } from "@/components/clearCartModal/clearCartModal";
import useMediaQuery from "@/utils/useMediaQuery";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import { GiftCard } from "@/dto/giftCard";
import { ServiceCartItem } from "@/models/cart/types";
import { LocalStorageKey } from "@/utils/storageKeys";
import {
  GIFT_AMOUNT_MIN,
  GIFT_AMOUNT_MAX,
  GIFT_AMOUNT_DEFAULT,
} from "@/utils/giftCardUtils";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { GetMerchantPaymentConfigResponse } from "@/dataLayer/getMerchantPaymentConfig";
import { Surcharge } from "@/components/surcharge/surcharge";
import { useCartDrawer } from "@/contexts/CartDrawerContext";
import { IGiftCardFormFields } from "../types";
import { useNativeActions } from "@/utils/useNativeActions";
import { useMerchantColor } from "@/utils/hooks/useMerchantColor";

interface IFormContainer {
  merchant: Merchant;
  merchantPaymentConfig?: GetMerchantPaymentConfigResponse;
}

export const FormContainer = ({
  merchant,
  merchantPaymentConfig,
}: IFormContainer) => {
  const form = useForm<IGiftCardFormFields>({
    mode: "all",
    defaultValues: {
      amount: GIFT_AMOUNT_DEFAULT,
    },
  });

  const { isCmaApp, appContentRendered } = useNativeActions();

  useEffect(() => {
    if (isCmaApp) {
      appContentRendered();
    }
  }, []);

  const {
    formState: { isValid },
    getValues,
  } = form;
  const { openCartDrawer } = useCartDrawer();

  const [showRecipientInformation, setShowRecipientInformation] =
    useState<boolean>(true);
  const [cardAmount, setCardAmount] = useState(GIFT_AMOUNT_DEFAULT);
  const [isRequired, setIsRequired] = useState<boolean>(true);
  const [showReplaceGiftCardModal, setShowReplaceGiftCardModal] =
    useState<boolean>(false);
  const [pendingGiftCard, setPendingGiftCard] = useState<any>(null);
  const [showClearCartModal, setShowClearCartModal] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string>("");

  const canShowSurcharge =
    merchant.isStandaloneGiftCardWebsite && merchantPaymentConfig;

  const currencyCode = getCurrencyCodeFromCountry(
    merchant.address.country as Countries
  );
  const merchantColor = useMerchantColor();
  const isPreviewSite =
    typeof window !== "undefined" &&
    window?.location?.hostname.startsWith("preview-");
  const sellingGiftCardsOnly =
    merchant.isStandaloneGiftCardWebsite && !isPreviewSite;
  const giftCardSellingEnabled = merchant?.isSellingGiftCards;
  const router = useRouter();
  let checkoutDisabled = !isValid;

  const handleCheck = () => {
    setShowRecipientInformation(!showRecipientInformation);
    setIsRequired(!showRecipientInformation);
  };

  const handleAddGiftCard = () => {
    const newGiftCardObject = {
      amount: getValues("amount"),
      note: getValues("personalMessage"),
      recipient: {
        name: getValues("recipientName"),
        email: getValues("recipientEmail"),
        isCustomer: !showRecipientInformation,
      },
    };
    const existingGiftCard = getFromLocalStorage<GiftCard | null>(
      LocalStorageKey.GIFT_CARD,
      null
    );
    const serviceCart = getFromLocalStorage<ServiceCartItem[]>(
      LocalStorageKey.SERVICE_CART,
      []
    );
    const shouldTriggerClearCart =
      (merchant.isService || merchant.isHybrid) && serviceCart.length > 0;

    if (existingGiftCard) {
      setPendingGiftCard(newGiftCardObject);
      setShowReplaceGiftCardModal(true);
      return;
    }

    if (shouldTriggerClearCart) {
      setPendingGiftCard(newGiftCardObject);
      openClearCartModal();
      return;
    }

    finalizeGiftCardAddition(newGiftCardObject);
  };

  const replaceGiftCard = () => {
    if (pendingGiftCard) {
      saveToLocalStorage(LocalStorageKey.GIFT_CARD, pendingGiftCard);
      setShowReplaceGiftCardModal(false);
      setPendingGiftCard(null);
      if (sellingGiftCardsOnly || merchant.isEssentialMerchant) {
        router.push("/guest-checkout");
      } else {
        openCartDrawer();
        if (merchant.isRetail || merchant.isService) {
          window.dispatchEvent(
            new StorageEvent("storage", {
              key: LocalStorageKey.RETAIL_CART_SYNC,
            })
          );
        } else if (merchant.isRestaurant) {
          window.dispatchEvent(
            new StorageEvent("storage", {
              key: LocalStorageKey.RESTAURANT_CART_SYNC,
            })
          );
        }
      }
    }
  };

  const finalizeGiftCardAddition = (giftCardObject: any) => {
    saveToLocalStorage(LocalStorageKey.GIFT_CARD, giftCardObject);
    if (sellingGiftCardsOnly || merchant.isEssentialMerchant) {
      router.push("/guest-checkout");
    } else {
      openCartDrawer();
      if (merchant.isRetail || merchant.isService) {
        window.dispatchEvent(
          new StorageEvent("storage", { key: LocalStorageKey.RETAIL_CART_SYNC })
        );
      } else if (merchant.isRestaurant) {
        window.dispatchEvent(
          new StorageEvent("storage", {
            key: LocalStorageKey.RESTAURANT_CART_SYNC,
          })
        );
      }
    }
  };

  const openClearCartModal = () => {
    setShowClearCartModal(true);
  };

  const closeClearCartModal = () => {
    setShowClearCartModal(false);
    setPendingGiftCard(null);
  };

  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const amountData = {
    name: "GiftCardAmount",
    min: GIFT_AMOUNT_MIN,
    max: GIFT_AMOUNT_MAX,
    locale: merchant.locale,
    currency: currencyCode,
    form: form,
    giftCardSellingEnabled: giftCardSellingEnabled,
    onBlurred: (amount: number) => {
      setCardAmount(amount);
    },
    setInfoMessage,
    merchantColor,
  };
  const buyThisForMyself = {
    handleCheckbox: handleCheck,
    giftCardSellingEnabled,
    showRecipientInformation,
    merchantColor,
  };
  const recipientInformationData = {
    form,
    isRequired,
    giftCardSellingEnabled,
    merchantColor,
  };
  const addGiftCardButtonData = {
    name: "GiftCardButton",
    merchant,
    checkoutDisabled,
    merchantColor,
    handleAddGiftCard,
  };
  const giftCardImageData = {
    name: "GiftCardImage",
    merchant,
    locale: merchant.locale,
    currency: currencyCode,
  };
  const surchargeData = {
    canShowSurcharge,
    surchargeRate: merchantPaymentConfig?.surcharging?.rate,
  };

  const order = useMemo(() => {
    const desktopOrder: any[] = [
      {
        id: "page-heading",
        column: 1,
        renderer: (props: any) => <PageHeading {...props} />,
        data: {},
      },
      {
        id: "amount-component",
        column: 1,
        renderer: (props: any) => <AmountComponent {...props} />,
        data: amountData,
      },
      {
        id: "buy-for-myself",
        column: 1,
        renderer: (props: any) => <BuyThisForMyselfComponent {...props} />,
        data: buyThisForMyself,
      },
      {
        id: "recipient-info",
        column: 1,
        renderer: (props: any) =>
          showRecipientInformation && (
            <RecipientInformationComponent {...props} />
          ),
        data: recipientInformationData,
      },
      {
        column: 2,
        renderer: (props: any) =>
          canShowSurcharge && (
            <div className="flex w-full justify-end items-end pb-2">
              <Surcharge {...props} />
            </div>
          ),
        data: surchargeData,
      },
      {
        id: "gift-card-image",
        column: 2,
        renderer: GiftCardImage,
        data: giftCardImageData,
      },
      {
        id: "add-button",
        column: 2,
        renderer: (props: any) => <AddGiftCardButton {...props} />,
        data: addGiftCardButtonData,
      },
    ];

    const mobileOrder: any[] = [
      {
        column: 1,
        renderer: (props: any) =>
          canShowSurcharge && (
            <div className="flex w-full justify-center items-center">
              <Surcharge {...props} />
            </div>
          ),
        data: surchargeData,
      },
      {
        id: "gift-card-image",
        column: 1,
        renderer: GiftCardImage,
        data: giftCardImageData,
      },
      {
        id: "page-heading",
        column: 1,
        renderer: (props: any) => <PageHeading {...props} />,
        data: {},
      },
      {
        id: "amount-component",
        column: 1,
        renderer: (props: any) => <AmountComponent {...props} />,
        data: amountData,
      },
      {
        id: "buy-for-myself",
        column: 1,
        renderer: (props: any) => <BuyThisForMyselfComponent {...props} />,
        data: buyThisForMyself,
      },
      {
        id: "recipient-info",
        column: 1,
        renderer: (props: any) =>
          showRecipientInformation && (
            <RecipientInformationComponent {...props} />
          ),
        data: recipientInformationData,
      },
      {
        id: "add-button",
        column: 1,
        renderer: (props: any) => <AddGiftCardButton {...props} />,
        data: addGiftCardButtonData,
      },
    ];
    return isMobileScreen ? mobileOrder : desktopOrder;
  }, [isMobileScreen, showRecipientInformation, cardAmount]);

  return (
    <div
      data-testid={applyDataTestId(pom.giftCard.formContainer)}
      className="flex pt-8 gap-20 pb-10 p-8"
    >
      <div className="flex flex-col gap-5 flex-1">
        {order.map((c: any) => {
          const Component = c.renderer;
          const props = {
            ...c.data,
            ...(c.data?.name === "GiftCardImage" && {
              amount: form.watch("amount"),
            }),
            ...(c.data?.name === "GiftCardButton" && {
              checkoutDisabled: checkoutDisabled,
            }),
            ...(c.data?.name === "GiftCardAmount" && {
              infoMessage: infoMessage,
            }),
          };

          return c.column === 1 ? <Component key={c.id} {...props} /> : null;
        })}
      </div>
      {order.some((c) => c.column === 2) && (
        <div className="flex-1">
          {order.map((c: any) => {
            const Component = c.renderer;
            const props = {
              ...c.data,
              ...(c.data?.name === "GiftCardImage" && {
                amount: form.watch("amount"),
              }),
              ...(c.data?.name === "GiftCardButton" && {
                checkoutDisabled: checkoutDisabled,
              }),
              ...(c.data?.name === "GiftCardAmount" && {
                infoMessage: infoMessage,
              }),
            };

            return c.column === 2 ? <Component key={c.id} {...props} /> : null;
          })}
        </div>
      )}
      <ReplaceGiftCardModal
        isOpen={showReplaceGiftCardModal}
        onRequestClose={() => setShowReplaceGiftCardModal(false)}
        merchantColor={merchantColor}
        replaceGiftCard={replaceGiftCard}
      />
      {showClearCartModal && (
        <ClearCartModal
          isOpen={showClearCartModal}
          onRequestClose={closeClearCartModal}
          merchantColor={merchantColor}
          addToCart={() => {
            finalizeGiftCardAddition(pendingGiftCard);
            closeClearCartModal();
          }}
        />
      )}
    </div>
  );
};
